import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, MapPin, Users, Sparkles, Smartphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useTestFlight } from "@/context/TestFlightContext";
import { GOOGLE_PLAY_URL } from "@/lib/testflight";

/**
 * LiveRoomModal — deep-link card for a single live venue. Shows the venue,
 * the GateLink code, and the fastest way for a visitor to get into that
 * room: open TestFlight (if the beta gate is unlocked) or claim their spot
 * on the waitlist.
 */
export default function LiveRoomModal({ venue, open, onOpenChange }) {
  const { openTestFlight, testFlightReady } = useTestFlight();
  if (!venue) return null;

  const gatelink = venue.gatelink_code || "";
  const cta = testFlightReady ? "Get GateMate" : "Get notified at launch";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="live-room-modal"
        className="max-w-md w-[92vw] bg-[#0B0F14] border border-white/10 shadow-[0_40px_80px_rgba(0,198,174,0.25)] rounded-3xl p-0 overflow-hidden [&>button]:hidden"
      >
        <VisuallyHidden asChild>
          <DialogTitle>{`Live now at ${venue.name}`}</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription>
            {`Open ${venue.name}'s room to see who's here.`}
          </DialogDescription>
        </VisuallyHidden>

        {/* Hero image / gradient */}
        <div className="relative h-40 overflow-hidden">
          {venue.photo_url ? (
            <img
              src={venue.photo_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-br from-neon-cyan/40 via-neon-violet/30 to-black" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0B0F14] to-transparent" />
          <span className="absolute top-3 left-4 inline-flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur-sm px-2.5 py-1 text-[11px] font-semibold text-neon-cyan ring-1 ring-neon-cyan/40">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-neon-cyan opacity-70 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-neon-cyan" />
            </span>
            {venue.live_now} live · now
          </span>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close live room card"
            data-testid="live-room-close"
            className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full bg-black/70 ring-1 ring-white/15 text-white hover:bg-neon-cyan hover:text-black transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 -mt-6 relative z-10">
          <p className="text-[11px] uppercase tracking-[0.24em] text-neon-cyan font-semibold">
            Live in DFW
          </p>
          <h3
            className="font-display text-2xl font-bold text-white leading-tight mt-1"
            data-testid="live-room-venue-name"
          >
            {venue.name}
          </h3>
          {venue.neighborhood ? (
            <p className="text-sm text-zinc-400 flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5" />
              {venue.neighborhood}
            </p>
          ) : null}

          {/* GateLink code chip */}
          {gatelink ? (
            <div className="mt-5 flex items-center justify-between gap-3 glass rounded-xl px-4 py-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">
                  Room code
                </p>
                <p
                  className="font-mono text-lg font-bold text-neon-cyan tracking-widest mt-0.5"
                  data-testid="live-room-gatelink-code"
                >
                  {gatelink}
                </p>
              </div>
              <Users className="w-6 h-6 text-white/40" />
            </div>
          ) : null}

          {/* Primary CTA */}
          <button
            type="button"
            onClick={() => {
              openTestFlight();
              onOpenChange(false);
            }}
            data-testid="live-room-testflight-cta"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full h-12 px-6 text-sm font-semibold bg-gradient-to-r from-neon-cyan to-neon-violet text-black hover:opacity-90 transition-opacity"
          >
            <Smartphone className="w-4 h-4" />
            {cta}
          </button>

          {GOOGLE_PLAY_URL ? (
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="live-room-googleplay-cta"
              className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 text-xs text-zinc-400 hover:text-neon-cyan transition-colors"
            >
              On Android? Get GateMate on Google Play →
            </a>
          ) : null}

          {/* Secondary — see full venue page */}
          <Link
            to={`/venues/${venue.slug}`}
            onClick={() => onOpenChange(false)}
            data-testid="live-room-full-page"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full h-11 px-6 text-sm font-medium text-white/80 hover:text-white glass hover:glass-cyan transition-all"
          >
            See the full venue page
            <ArrowRight className="w-4 h-4" />
          </Link>

          <p className="mt-4 text-[11px] text-zinc-500 text-center flex items-center justify-center gap-1.5">
            <Sparkles className="w-3 h-3 text-neon-cyan" />
            Nothing on your profile is shown until you tap in inside the room.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
